import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import axios from "axios";
import Cookies from "js-cookie";
import "../../styles/Login.css";
import authStore from "../../utils/authStorage";

/**
 * Robustly read ?verified=1 whether using BrowserRouter or HashRouter.
 */
function useVerifiedFlag() {
  const location = useLocation();

  const wasJustVerified = useMemo(() => {
    const searchParams = new URLSearchParams(location.search || "");
    let v = searchParams.get("verified");

    if (!v && location.hash) {
      const hash = location.hash.startsWith("#")
        ? location.hash.slice(1)
        : location.hash;
      const qIndex = hash.indexOf("?");
      if (qIndex !== -1) {
        const hashQs = new URLSearchParams(hash.slice(qIndex));
        v = hashQs.get("verified");
      }
    }

    if (typeof v !== "string") return false;
    const val = v.toLowerCase();
    return val === "1" || val === "true" || val === "yes";
  }, [location.search, location.hash]);

  return wasJustVerified;
}

export default function Login() {
  const [userid, setUserid] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [msg, setMsg] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const wasJustVerified = useVerifiedFlag();

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

  const handleLogin = async (e) => {
    e.preventDefault();
    setMsg("");

    try {
      const res = await axios.post(`${BASE_URL}/api/auth/login`, {
        userid,
        password,
      });

      authStore.userId = String(res.data.id);
      authStore.userRole = String(res.data.role);
      authStore.token = res.data.token;
      authStore.cancelPendingLogout();
      authStore.ensureTabSession();

      try {
        const meResp = await axios.get(
          `${BASE_URL}/api/candidate/user/profile`,
          { headers: { Authorization: `Bearer ${res.data.token}` } },
        );
        authStore.me = meResp.data;

        const params = new URLSearchParams(location.search);
        const redirect = params.get("redirect");

        if (meResp.data.pipeline_status === 2) {
          try {
            const activeApp = await axios.get(`${BASE_URL}/api/apply/check`, {
              headers: { Authorization: `Bearer ${res.data.token}` },
            });

            if (activeApp.data?.application_id) {
              navigate(
                `/candidate/onboarding/${activeApp.data.application_id}`,
                {
                  replace: true,
                },
              );
            } else {
              navigate("/", { replace: true });
            }
          } catch (err) {
            console.error("Failed to fetch active application:", err);
            navigate("/", { replace: true });
          }
        } else if (meResp.data.pipeline_status === 3) {
          navigate("/", { replace: true });
        } else {
          const applyState = await axios.get(`${BASE_URL}/api/apply/check`, {
            headers: { Authorization: `Bearer ${res.data.token}` },
          });
          const selectedJobId =
            applyState.data?.selected_job_id || Cookies.get("pendingJobId");

          if (selectedJobId) {
            Cookies.remove("pendingJobId");
            navigate(`/apply/${selectedJobId}`, { replace: true });
          } else if (redirect) {
            navigate(redirect, { replace: true });
          } else {
            navigate("/", { replace: true });
          }
        }

        window.dispatchEvent(new Event("profileUpdated"));
      } catch (err) {
        console.error("Profile fetch failed:", err);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setMsg(err.response?.data?.message || "Login failed");
    }
  };

  return (
    <div className="auth-bg">
      <div className="login-card">
        <h3 className="auth-title">Login</h3>

        {wasJustVerified && (
          <div
            className="auth-success"
            style={{
              marginBottom: 10,
              color: "#155724",
              background: "#d4edda",
              padding: "10px 16px",
              borderRadius: 6,
            }}
          >
            Your email has been verified. You can now log in.
          </div>
        )}

        <form onSubmit={handleLogin} className="auth-form" noValidate>
          <label>Email</label>
          <input
            className="form-control"
            value={userid}
            onChange={(e) => setUserid(e.target.value)}
            required
            autoComplete="username"
          />

          <label>Password</label>
          <div className="input-group">
            <input
              type={passwordVisible ? "text" : "password"}
              className="form-control"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
            <button
              type="button"
              className="input-group-text"
              onClick={() => setPasswordVisible(!passwordVisible)}
              aria-label={passwordVisible ? "Hide password" : "Show password"}
            >
              {passwordVisible ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          <button type="submit" className="btn btn-primary auth-submit">
            Login
          </button>
        </form>

        {(msg || location.state?.sessionExpired) && (
          <p className="auth-error">
            {msg || "Your session expired. Please log in again."}
          </p>
        )}

        <div className="auth-links">
          <Link to="/register" className="link-cta">
            Create account
          </Link>
          <Link to="/forgot-password" className="link-muted">
            Forgot password?
          </Link>
        </div>
      </div>
    </div>
  );
}
